import { useEffect, useRef, useState } from 'react';
import { Loader2, MessageCircle, Send, X } from 'lucide-react';
import { hairline, pickReadable, withTenant, type Site } from './BookingPage';

/*
 * The chat box on the booking page (#250's front door, built 2026-09-24).
 *
 * Everything behind it — `POST /api/public/brain`, the booking brain, holds — shipped on
 * 2026-09-11 and was never reachable: no page called the endpoint, so the CRM switch for it
 * ("Chat on your booking page") turned on nothing a visitor could see.
 *
 * ⚠️ SHOWN ONLY WHEN SHE HAS SWITCHED IT ON. The route answers 404 otherwise, and the page must
 * not advertise a feature the business never chose (see migration 0059).
 *
 * ⚠️ IT NEVER BOOKS ANYONE, AND THE COPY SAYS SO. Picking a time takes a thirty-minute HOLD, and
 * the hand-off is the same `?hold=` link the texting assistant sends: a reload that preselects
 * the service and the time in the form below, where the customer still answers the intake
 * questions and presses Book. Reusing that path is deliberate — it is the tested one, and a
 * second, in-page way of applying a hold would be a second thing to keep in step with it.
 *
 * ⚠️ LABELLED AS AN AI ASSISTANT, by name, on the button and in the header. A visitor must never
 * be unsure whether they are talking to the owner.
 */

type Offer = { startsAt: string; label: string; date: string };
type Line =
  | { from: 'them'; text: string }
  | { from: 'milo'; text: string; offers?: Offer[]; holdToken?: string | null };

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * "Tuesday, October 1 at 10:00 AM". `date` and `label` are already on HER clock, so this is
 * calendar arithmetic only — no timezone conversion, which is what would move it by a day.
 */
function offerText(o: Offer): string {
  const [y, m, d] = o.date.split('-').map(Number) as [number, number, number];
  const weekday = DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  const [hh, mm] = o.label.split(':').map(Number) as [number, number];
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${weekday}, ${MONTHS[m - 1]} ${d} at ${h12}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`;
}

/** The booking page again, holding their time — see the note at the top of the file. */
function finishUrl(token: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set('hold', token);
  return url.toString();
}

export default function AssistantChat({ site }: { site: Site }) {
  const { accentColor, backgroundColor, textColor } = site.branding;
  const onAccent = pickReadable(accentColor);
  const line = hairline(textColor);
  const business = site.business.name;

  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const conversationId = useRef<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [lines, busy]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  async function send(text: string) {
    const t = text.trim().slice(0, 1500);
    if (!t || busy) return;
    setLines((l) => [...l, { from: 'them', text: t }]);
    setDraft('');
    setNotice(null);
    setBusy(true);
    try {
      const r = await fetch(withTenant('/api/public/brain'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: t, ...(conversationId.current ? { conversationId: conversationId.current } : {}) }),
      });
      if (r.status === 404) {
        // Switched off since the page loaded. Say so once, and stop offering the box.
        setUnavailable(true);
        return;
      }
      if (r.status === 429) {
        setNotice("You've sent a lot of messages. Give it a few minutes, or book using the form on this page.");
        return;
      }
      if (!r.ok) throw new Error(String(r.status));
      const body = (await r.json()) as {
        conversationId: string;
        reply: string;
        offers: Offer[];
        holdToken: string | null;
      };
      conversationId.current = body.conversationId;
      setLines((l) => [...l, { from: 'milo', text: body.reply, offers: body.offers, holdToken: body.holdToken }]);
    } catch {
      setNotice("That didn't go through. Try again, or book using the form on this page.");
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  // Only the newest message's times are pickable: older offers may already be gone.
  const lastIndex = lines.length - 1;

  if (!open) {
    return (
      <>
        {/* Room under the page so the floating button never sits on top of its last control. */}
        <div aria-hidden className="h-20" />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full px-4 py-3 text-sm font-semibold shadow-lg"
          style={{ backgroundColor: accentColor, color: onAccent }}
        >
          <MessageCircle className="h-5 w-5" />
          Questions? Ask Milo
        </button>
      </>
    );
  }

  return (
    <div
      role="dialog"
      aria-label={`Chat with Milo, the AI booking assistant for ${business}`}
      className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col rounded-t-2xl shadow-2xl sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-96 sm:rounded-2xl"
      style={{ backgroundColor, color: textColor, border: `1px solid ${line}` }}
    >
      <div className="flex items-start justify-between gap-3 border-b px-4 py-3" style={{ borderColor: line }}>
        <div>
          <div className="text-sm font-semibold">Milo</div>
          <div className="text-xs opacity-70">AI booking assistant for {business}</div>
        </div>
        <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" className="rounded-md p-1 opacity-70 hover:opacity-100">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm" aria-live="polite">
        {/*
          A hint, not a greeting bubble: the server's first reply introduces Milo itself, so a
          client-side "Hi, I'm Milo" made him introduce himself twice.
        */}
        {lines.length === 0 && (
          <p className="text-xs opacity-70">
            Ask about prices, what&rsquo;s included, or when there&rsquo;s an opening. Milo can hold a time for you.
          </p>
        )}

        {lines.map((l, i) =>
          l.from === 'them' ? (
            <Bubble key={i} side="them" line={line} accent={accentColor} onAccent={onAccent}>
              {l.text}
            </Bubble>
          ) : (
            <div key={i} className="space-y-2">
              <Bubble side="milo" line={line}>
                {l.text}
              </Bubble>
              {l.holdToken ? (
                <a
                  href={finishUrl(l.holdToken)}
                  className="block rounded-lg px-4 py-2.5 text-center text-sm font-semibold"
                  style={{ backgroundColor: accentColor, color: onAccent }}
                >
                  Finish booking
                </a>
              ) : (
                i === lastIndex &&
                l.offers &&
                l.offers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {l.offers.map((o) => (
                      <button
                        key={o.startsAt}
                        type="button"
                        disabled={busy}
                        onClick={() => void send(`${offerText(o)} works for me`)}
                        className="rounded-full border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                        style={{ borderColor: accentColor }}
                      >
                        {offerText(o)}
                      </button>
                    ))}
                  </div>
                )
              )}
            </div>
          ),
        )}

        {busy && (
          <div className="flex items-center gap-2 text-xs opacity-70">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Milo is typing…
          </div>
        )}
        {notice && <p className="text-xs opacity-80">{notice}</p>}
        {unavailable && <p className="text-xs opacity-80">Chat isn&rsquo;t available right now. You can still book using the form on this page.</p>}
        <div ref={endRef} />
      </div>

      {!unavailable && (
        <form
          className="border-t px-3 py-3"
          style={{ borderColor: line }}
          onSubmit={(e) => {
            e.preventDefault();
            void send(draft);
          }}
        >
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 1500))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(draft);
                }
              }}
              rows={1}
              placeholder="e.g. What's your first opening next week?"
              aria-label="Your message"
              className="max-h-28 flex-1 resize-none rounded-lg border bg-transparent px-3 py-2 text-sm"
              style={{ borderColor: line, color: textColor }}
            />
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              aria-label="Send"
              className="rounded-lg p-2.5 disabled:opacity-40"
              style={{ backgroundColor: accentColor, color: onAccent }}
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2 text-[11px] leading-snug opacity-60">
            Milo is an AI. It uses {business}&rsquo;s real prices and open times, and it can hold a time for you, but you
            finish the booking yourself.
          </p>
        </form>
      )}
    </div>
  );
}

function Bubble({
  side,
  line,
  accent,
  onAccent,
  children,
}: {
  side: 'them' | 'milo';
  line: string;
  accent?: string;
  onAccent?: string;
  children: React.ReactNode;
}) {
  return side === 'them' ? (
    <div className="flex justify-end">
      <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm px-3 py-2" style={{ backgroundColor: accent, color: onAccent }}>
        {children}
      </div>
    </div>
  ) : (
    <div className="flex justify-start">
      <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-bl-sm border px-3 py-2" style={{ borderColor: line }}>
        {children}
      </div>
    </div>
  );
}
